/* eslint-disable class-methods-use-this */
import { DemeterDataFrequency, MarketIndicatorFactorDataModel } from '../../../../Generated/Raven-Demeter';
import formattingService from '../../../Services/Formatting/FormattingService';
import { IChartAreaRangeData, IChartData } from '../ChartDefinitions';
import { defaultSeasonalParameters } from './MarketIndicatorChartDefaultParameters';
import { ISeasonalSeries } from './MarketIndicatorUserChartDefinitions';

class MarketIndicatorChartService {
    // Helper method to calculate the entry key for data series mapping based on data frequency.
    // Create consistent keys for grouping data points across different time periods (daily, weekly, monthly).
    private calculateEntryKey(asOfDate: Date, dataFrequency: DemeterDataFrequency): number {
        if (dataFrequency === DemeterDataFrequency.Daily) {
            return formattingService.toWeekNumber(asOfDate);
        }

        if (dataFrequency === DemeterDataFrequency.Weekly) {
            return formattingService.toWeekNumber(asOfDate);
        }

        if (dataFrequency === DemeterDataFrequency.Monthly) {
            return asOfDate.getMonth();
        }

        return formattingService.toWeekNumber(asOfDate);
    }

    // Calculates date ranges and determines year logic for seasonal chart processing.
    // Establish start/end dates, week numbers, and determine if data spans multiple years or is limited to last year only.
    private calculateDateRanges(chartStartMonth?: string, chartEndMonth?: string, uniqueChartData?: IChartData[]) {
        const startMonth = chartStartMonth ?? defaultSeasonalParameters.startMonth;
        const startDateSelection = new Date(new Date(new Date().setDate(1)).setMonth(+startMonth - 1));
        const startWeekNumber = startDateSelection.getMonth() === 0 ? 1 : formattingService.toWeekNumber(startDateSelection);

        const endMonth = chartEndMonth ?? defaultSeasonalParameters.endMonth;
        const endDateSelection = new Date(new Date(new Date().setDate(1)).setMonth(+endMonth));
        const endWeekNumber = endDateSelection.getMonth() === 11 || endMonth === '12' ? 52 : formattingService.toWeekNumber(endDateSelection) - 1;

        const lastMonthOfCalculationData = (uniqueChartData?.[uniqueChartData.length - 1]?.asOfDate.getMonth() ?? -1) + 1;
        const startMonthIsAfterCurrentData = lastMonthOfCalculationData < +startMonth;
        const isLastYearOnly = +startMonth < +endMonth && startMonthIsAfterCurrentData;

        return {
            startMonth,
            endMonth,
            startDateSelection,
            endDateSelection,
            startWeekNumber,
            endWeekNumber,
            startMonthIsAfterCurrentData,
            isLastYearOnly,
        };
    }

    // Adjusts data and date selections based on year logic for seasonal analysis.
    // Handle cases where data spans across years or when the selected date range is only available in the previous year.
    private adjustDataForYearLogic(
        uniqueChartData: IChartData[],
        startDateSelection: Date,
        endDateSelection: Date,
        isLastYearOnly: boolean,
        startMonthIsAfterCurrentData: boolean,
    ): void {
        if (isLastYearOnly) {
            startDateSelection.setFullYear(startDateSelection.getFullYear() - 1);
            endDateSelection.setFullYear(endDateSelection.getFullYear() - 1);
            const lastIndex = uniqueChartData.findIndex((x) => x.asOfDate.getTime() > endDateSelection.getTime());
            uniqueChartData.splice(lastIndex);
        }

        if (startMonthIsAfterCurrentData && !isLastYearOnly) {
            startDateSelection.setFullYear(startDateSelection.getFullYear() - 1);
        }
    }

    // Calculates slice indexes for dividing data by years based on start month/week.
    // Identify data boundaries for each year in the dataset, enabling proper seasonal comparison across multiple years.
    private calculateWeekSliceIndexes(
        uniqueChartData: IChartData[],
        dataFrequency: DemeterDataFrequency,
        startWeekNumber: number,
        startMonth: string,
    ): number[] {
        let lastPushedYear: number | null = null;
        const slices: number[] = [];

        uniqueChartData.forEach((current, index) => {
            const currentWeekNumber = formattingService.toWeekNumber(current.asOfDate);
            const currentYear = current.asOfDate.getFullYear();

            if (dataFrequency === DemeterDataFrequency.Daily) {
                // Only push the first occurrence for each year where week >= startWeekNumber
                if (currentWeekNumber >= startWeekNumber && currentYear !== lastPushedYear) {
                    slices.push(index);
                    lastPushedYear = currentYear;
                }
            }

            if (dataFrequency === DemeterDataFrequency.Weekly) {
                if (currentWeekNumber >= startWeekNumber && currentYear !== lastPushedYear) {
                    slices.push(index);
                    lastPushedYear = currentYear;
                }
            }

            if (dataFrequency === DemeterDataFrequency.Monthly) {
                if (current.asOfDate.getMonth() === +startMonth - 1 && currentYear !== lastPushedYear) {
                    slices.push(index);
                    lastPushedYear = currentYear;
                }
            }
        });

        return slices.sort((a, b) => b - a);
    }

    // Identifies the anchor year data and returns formatted year arrays with trimmed data to match anchor.
    // Uses the anchor year as the template for all other years' data structure and timing.
    private processYearsWithAnchor(
        arrayOfYears: IChartData[][],
        isLastYearOnly: boolean,
        dataFrequency: DemeterDataFrequency,
        endMonth: string,
    ): { anchorYear: IChartData[]; processedYears: IChartData[][]; anchorIndex: number } {
        const anchorIndex = isLastYearOnly ? 0 : 1;
        const anchorYear = arrayOfYears[anchorIndex];

        const processedYears = arrayOfYears.map((yearData, index) => {
            if (index === anchorIndex) {
                return yearData;
            }

            const trimmedYear = [...yearData];
            if (dataFrequency === DemeterDataFrequency.Weekly && trimmedYear.length > anchorYear.length) {
                const lastElement = trimmedYear[trimmedYear.length - 1];
                const lastElementMonth = lastElement.asOfDate.getMonth() + 1;

                if (lastElementMonth > +endMonth) {
                    trimmedYear.pop();
                } else {
                    trimmedYear.shift();
                }
            }

            return trimmedYear;
        });

        return { anchorYear, processedYears, anchorIndex };
    }

    // Creates arrays of yearly data by slicing the unique chart data using calculated indexes.
    // Organize data into separate arrays for each year, where arrayOfYears[0] is thisYear data and arrayOfYears[1] is lastYear data.
    private createArrayOfYears(weekSliceIndexes: number[], uniqueChartData: IChartData[]): IChartData[][] {
        return weekSliceIndexes.map((x, i, a) => {
            if (i === 0) {
                return uniqueChartData.slice(x);
            }

            return uniqueChartData.slice(x, a[i - 1]);
        });
    }

    // Populates data series using anchor year as template and syncs all other years by index.
    // Eliminates the need for complex entry key matching by using direct index alignment.
    private populateDataSeriesFromAnchor(
        processedYears: IChartData[][],
        anchorIndex: number,
        dataFrequency: DemeterDataFrequency,
        isLastYearOnly: boolean,
        numberOfYears: number,
    ): ISeasonalSeries {
        const dataSeriesHelper = new Map<
            number,
            { thisYearDataPoint: IChartData | null; lastYearDataPoint: IChartData | null; aggregateData: IChartData[]; referenceDate?: Date }
        >();
        const anchorYear = processedYears[anchorIndex];

        anchorYear.forEach((anchorItem) => {
            if (!anchorItem?.asOfDate) {
                return;
            }

            const entryKey = this.calculateEntryKey(anchorItem.asOfDate, dataFrequency);
            dataSeriesHelper.set(entryKey, {
                thisYearDataPoint: null,
                lastYearDataPoint: anchorItem,
                aggregateData: [],
                referenceDate: anchorItem.asOfDate,
            });
        });

        processedYears.forEach((yearData, yearIndex) => {
            if (yearIndex > numberOfYears) {
                return;
            }

            yearData.forEach((item, itemIndex) => {
                const anchorItem = anchorYear[itemIndex];
                if (!anchorItem?.asOfDate) {
                    return;
                }

                const entryKey = this.calculateEntryKey(anchorItem?.asOfDate, dataFrequency);
                const dataEntry = dataSeriesHelper.get(entryKey);

                if (dataEntry) {
                    if (yearIndex === 0 && !isLastYearOnly) {
                        if (dataEntry) {
                            dataEntry.thisYearDataPoint = item;
                        }
                    }

                    if (yearIndex !== 0 || isLastYearOnly) {
                        dataEntry.aggregateData.push(item);
                    }
                }
            });
        });

        return {
            thisYearLineSeries: [],
            lastYearLineSeries: [],
            dataSeriesHelper,
            nYearAverageLineSeries: [],
            areaRangeSeries: [],
        };
    }

    // Filters data series helper by removing unwanted keys based on seasonal date ranges.
    // Clean up the data series Map by removing entries that fall outside the specified start and end month/week boundaries.
    private filterDataSeriesHelper(
        dataSeriesHelper: Map<number, any>,
        dataFrequency: DemeterDataFrequency,
        startWeekNumber: number,
        endWeekNumber: number,
        startMonth: string,
        endMonth: string,
    ): void {
        dataSeriesHelper.delete(53);

        const allKeys = Array.from(dataSeriesHelper.keys()).sort((a, b) => a - b);
        const endNumber = dataFrequency !== DemeterDataFrequency.Monthly ? endWeekNumber : +endMonth - 1;
        const startNumber = dataFrequency !== DemeterDataFrequency.Monthly ? startWeekNumber : +startMonth - 1;

        allKeys.forEach((keyNumber) => {
            if (startNumber > endNumber) {
                if (keyNumber > endNumber && keyNumber < startNumber) {
                    dataSeriesHelper.delete(keyNumber);
                }
            } else if (keyNumber < startNumber || keyNumber > endNumber) {
                dataSeriesHelper.delete(keyNumber);
            }
        });
    }

    // Creates the final series data arrays from the processed data series helper Map.
    // Build the final thisYear, lastYear, nYearAverage, and areaRange series that will be returned for chart rendering.
    private createFinalSeries(dataForSeries: ISeasonalSeries): ISeasonalSeries {
        dataForSeries.thisYearLineSeries = Array.from(dataForSeries.dataSeriesHelper.entries())
            .map(([, group]) => {
                if (!group.thisYearDataPoint) {
                    return null;
                }
                return {
                    ...group.thisYearDataPoint,
                    asOfDate: group.referenceDate,
                    originalAsOfDate: group.thisYearDataPoint?.originalAsOfDate,
                };
            })
            .filter((dataPoint) => dataPoint !== null) as IChartData[];

        dataForSeries.lastYearLineSeries = Array.from(dataForSeries.dataSeriesHelper.entries())
            .map(([, group]) => group.lastYearDataPoint)
            .filter((dataPoint) => dataPoint !== null) as IChartData[];

        dataForSeries.nYearAverageLineSeries = Array.from(dataForSeries.dataSeriesHelper.entries()).map(([, group]) => {
            const dataByYear = new Map<number, IChartData[]>();

            group.aggregateData.forEach((dataPoint) => {
                const year = dataPoint.asOfDate.getFullYear();
                if (!dataByYear.has(year)) {
                    dataByYear.set(year, []);
                }
                dataByYear.get(year)!.push(dataPoint);
            });

            const yearAverages: number[] = [];
            dataByYear.forEach((yearData) => {
                const yearSum = yearData.reduce((sum, item) => sum + item.value, 0);
                const yearAverage = yearSum / yearData.length;
                yearAverages.push(yearAverage);
            });
            const finalAverage = yearAverages.reduce((sum, average) => sum + average, 0) / yearAverages.length;

            return {
                value: finalAverage,
                asOfDate: group.referenceDate!,
                isActualValue: group.aggregateData[0]?.isActualValue,
                percentValue: group.aggregateData[0]?.percentValue,
                originalAsOfDate: group.aggregateData[0]?.originalAsOfDate,
            } as IChartData;
        });

        dataForSeries.areaRangeSeries = Array.from(dataForSeries.dataSeriesHelper.entries()).map(([, group]) => {
            const values = group.aggregateData.map((item) => item.value);
            const minimumValue = Math.min(...values);
            const maximumValue = Math.max(...values);

            return {
                minimumValue,
                maximumValue,
                asOfDate: group.referenceDate!,
                isActualValue: group.aggregateData[0]?.isActualValue,
                percentValue: group.aggregateData[0]?.percentValue,
                originalAsOfDate: group.aggregateData[0]?.originalAsOfDate,
            } as IChartAreaRangeData;
        });

        return dataForSeries;
    }

    convertToChartData(data: MarketIndicatorFactorDataModel[]): IChartData[] {
        const nowDate = new Date();
        return data
            .filter((x) => new Date(x.asOfDate) <= nowDate)
            .map(
                (row) =>
                    ({
                        value: row.value ?? 0,
                        asOfDate: new Date(row.asOfDate),
                        originalAsOfDate: new Date(row.asOfDate),
                        isActualValue: row.isActualValue,
                    } as IChartData),
            );
    }

    filterByYears(data: (IChartData | IChartAreaRangeData)[], yearsBack: number): any[] {
        if (!data || data.length === 0) {
            return [];
        }

        const currentDate = data[data.length - 1].asOfDate;
        const cutoffDate = new Date();
        cutoffDate.setFullYear(currentDate.getFullYear() - yearsBack);

        return data.filter((item) => item.asOfDate >= cutoffDate);
    }

    getSeasonalChartSeriesData = (
        factorData: MarketIndicatorFactorDataModel[],
        dataFrequency: DemeterDataFrequency,
        numberOfYears: number,
        chartStartMonth?: string,
        chartEndMonth?: string,
    ) => {
        const convertedChartData = this.convertToChartData(factorData);
        const uniqueChartData = convertedChartData.filter((item, index, array) => {
            const lastIndexOfDate = array.map((x) => x.asOfDate.getTime()).lastIndexOf(item.asOfDate.getTime());

            return index === lastIndexOfDate;
        });

        const dateRanges = this.calculateDateRanges(chartStartMonth, chartEndMonth, uniqueChartData);

        this.adjustDataForYearLogic(
            uniqueChartData,
            dateRanges.startDateSelection,
            dateRanges.endDateSelection,
            dateRanges.isLastYearOnly,
            dateRanges.startMonthIsAfterCurrentData,
        );

        const weekSliceIndexes = this.calculateWeekSliceIndexes(uniqueChartData, dataFrequency, dateRanges.startWeekNumber, dateRanges.startMonth);

        if (weekSliceIndexes.length < 1) {
            return null;
        }

        const arrayOfYears = this.createArrayOfYears(weekSliceIndexes, uniqueChartData);
        const { processedYears, anchorIndex } = this.processYearsWithAnchor(arrayOfYears, dateRanges.isLastYearOnly, dataFrequency, dateRanges.endMonth);
        const dataForSeries = this.populateDataSeriesFromAnchor(processedYears, anchorIndex, dataFrequency, dateRanges.isLastYearOnly, numberOfYears);

        this.filterDataSeriesHelper(
            dataForSeries.dataSeriesHelper,
            dataFrequency,
            dateRanges.startWeekNumber,
            dateRanges.endWeekNumber,
            dateRanges.startMonth,
            dateRanges.endMonth,
        );

        const finalSeriesData = this.createFinalSeries(dataForSeries);

        return finalSeriesData;
    };

    private calculateDateBufferRange = (baseDate: Date, dayBuffer: number): { startDate: number; endDate: number } => {
        const startDate = new Date(baseDate);
        startDate.setDate(startDate.getDate() - dayBuffer);
        const endDate = new Date(baseDate);
        endDate.setDate(endDate.getDate() + dayBuffer);

        return { startDate: startDate.getTime(), endDate: endDate.getTime() };
    };

    private getFocusDateForWeek = (year: number, weekNumber: number): Date => {
        const focusDate = new Date(year, 0, 1);
        if (weekNumber - 1 > 0) {
            focusDate.setDate((weekNumber - 1) * 7);
        }

        return focusDate;
    };

    private filterByWeekAndDateRange = (data: IChartData[], weekNumber: number, startDate: number, endDate: number): IChartData[] => {
        const filtered = data.filter((x) => x.asOfDate.getTime() >= startDate && x.asOfDate.getTime() <= endDate);

        return filtered.filter((x) => formattingService.toWeekNumberAverageSeasonal(x.asOfDate) === weekNumber);
    };

    private calculateAverageValue = (data: IChartData[]): number => {
        if (data.length === 0) {
            return 0;
        }

        return data.reduce((sum, x) => sum + (x.value ?? 0), 0) / data.length;
    };

    getAverageSeasonalChartSeriesData = (
        factorData: MarketIndicatorFactorDataModel[],
        numberOfYears: number,
        chartStartMonth?: string,
        chartEndMonth?: string,
    ): ISeasonalSeries => {
        const convertedChartData = this.convertToChartData(factorData).filter((x) => {
            const today = new Date().getDay();
            const todayIsAfterFriday = today > 5;
            const dayOfWeekIsBeforeToday = x.asOfDate.getDay() > today;
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).getTime();
            const dayIsBeforeSevenDaysAgo = x.asOfDate.getTime() < sevenDaysAgo;

            if (todayIsAfterFriday || dayOfWeekIsBeforeToday || dayIsBeforeSevenDaysAgo) {
                return true;
            }

            return false;
        });

        const currentYear = Math.max(...convertedChartData.map((x) => x.asOfDate.getFullYear()));
        const startYear = currentYear - numberOfYears;
        const endYear = currentYear - 1;
        const dayBuffer = 12;
        const startMonth = chartStartMonth ?? defaultSeasonalParameters.startMonth;
        const startDateSelection = new Date(new Date(new Date().setDate(1)).setMonth(+startMonth - 1));
        const startWeekNumber = startDateSelection.getMonth() === 0 ? 1 : formattingService.toWeekNumber(startDateSelection);
        const endMonth = chartEndMonth ?? defaultSeasonalParameters.endMonth;
        const endDateSelection = new Date(new Date(new Date().setDate(1)).setMonth(+endMonth));
        const endWeekNumber = endDateSelection.getMonth() === 11 || endMonth === '12' ? 52 : formattingService.toWeekNumber(endDateSelection);

        const dataSeriesHelper = new Map<
            number,
            { thisYearDataPoint: IChartData | null; lastYearDataPoint: IChartData | null; aggregateData: IChartData[]; referenceDate?: Date }
        >();

        for (let week = 1; week <= 52; week += 1) {
            const previousValues: number[] = [];
            let currentYearCurrentWeekData: IChartData | null = null;

            for (let year = startYear; year <= endYear; year += 1) {
                const focusDate = this.getFocusDateForWeek(year, week);
                const { startDate, endDate } = this.calculateDateBufferRange(focusDate, dayBuffer);

                const thisWeekData = this.filterByWeekAndDateRange(convertedChartData, week, startDate, endDate);

                if (thisWeekData.length > 0) {
                    const previousFocusDate = new Date(thisWeekData[0].asOfDate);
                    previousFocusDate.setDate(previousFocusDate.getDate() - 7);
                    const previousWeekNumber = formattingService.toWeekNumberAverageSeasonal(previousFocusDate);

                    const { startDate: previousStartDate } = this.calculateDateBufferRange(previousFocusDate, dayBuffer);
                    const previousEndDate = thisWeekData[0].asOfDate.getTime();

                    const previousFilteredData = convertedChartData.filter(
                        (x) => x.asOfDate.getTime() >= previousStartDate && x.asOfDate.getTime() <= previousEndDate,
                    );
                    const previousWeekData = previousFilteredData.filter(
                        (x) => formattingService.toWeekNumberAverageSeasonal(x.asOfDate) === previousWeekNumber,
                    );

                    const previousValue = this.calculateAverageValue(previousWeekData);
                    previousValues.push(previousValue);
                }
            }

            const currentYearFocusDate = this.getFocusDateForWeek(currentYear, week + 1);
            const { startDate: currentStartDate, endDate: currentEndDate } = this.calculateDateBufferRange(currentYearFocusDate, dayBuffer);
            const currentYearWeekData = this.filterByWeekAndDateRange(convertedChartData, week, currentStartDate, currentEndDate);

            if (currentYearWeekData.length > 0) {
                const currentWeekValue = this.calculateAverageValue(currentYearWeekData);

                currentYearCurrentWeekData = {
                    asOfDate: currentYearFocusDate,
                    value: currentWeekValue,
                    isActualValue: true,
                    originalAsOfDate: currentYearWeekData[currentYearWeekData.length - 1]?.asOfDate ?? currentYearFocusDate,
                };
            }

            if (previousValues.length > 0) {
                const previousAverage = previousValues.reduce((a, b) => a + b, 0) / previousValues.length;
                const focusDate = this.getFocusDateForWeek(endYear, week);

                dataSeriesHelper.set(week, {
                    thisYearDataPoint: currentYearCurrentWeekData,
                    lastYearDataPoint: null,
                    aggregateData: [
                        {
                            asOfDate: focusDate,
                            value: previousAverage,
                            isActualValue: true,
                            originalAsOfDate: focusDate,
                        },
                    ],
                    referenceDate: focusDate,
                });
            }
        }

        dataSeriesHelper.delete(53);
        const allKeys = Array.from(dataSeriesHelper.keys()).sort((a, b) => a - b);

        allKeys.forEach((weekNumber) => {
            if (+startMonth < +endMonth) {
                if (weekNumber < startWeekNumber || weekNumber > endWeekNumber) {
                    dataSeriesHelper.delete(weekNumber);
                }
            } else if (weekNumber < startWeekNumber && weekNumber > endWeekNumber) {
                dataSeriesHelper.delete(weekNumber);
            }
        });

        const result: ISeasonalSeries = {
            thisYearLineSeries: [],
            lastYearLineSeries: [],
            dataSeriesHelper,
            nYearAverageLineSeries: [],
            areaRangeSeries: [],
        };

        return this.createFinalSeries(result);
    };
}

const marketIndicatorChartService = new MarketIndicatorChartService();
export default marketIndicatorChartService;
